// eslint-disable-next-line no-restricted-imports
import {useSearchParams} from 'react-router-dom';

import {useInvoicingDocumentRecalculateMutation} from '@dms/api/finance';
import {businessCaseRoutes} from '@dms/routes';
import {useSubmitCorrectiveTaxDocument} from '@dms/shared';

import {composePath, useNavigate, useQueryState} from 'shared/web';

import {useAccountingCalculationCache} from '../../../context/useAccountingCalculationCache';
import {AccountingDocumentFormValues} from '../../../types';
import {getValidatedCheckoutSearchParams} from '../utils/getValidatedCheckoutSearchParams';
import {useGetCorrectiveTaxForBCDocumentBody} from './useGetCorrectiveTaxForBCDocumentBody';

export const useHandleSubmitCheckoutCorrectiveDocument = (
  invoicingDocumentId: string,
  isSellerAsSupplier = false
) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [invoiceType] = useQueryState('invoiceType');
  const [orderId] = useQueryState('orderId');
  const [checkoutId] = useQueryState('checkoutId');
  const [businessCaseId] = useQueryState('businessCaseId');

  const {accountingDocumentCalculationCacheKey} = useAccountingCalculationCache();
  const [, {data: summary}] = useInvoicingDocumentRecalculateMutation({
    fixedCacheKey: accountingDocumentCalculationCacheKey,
  });

  const [getRequestBody] = useGetCorrectiveTaxForBCDocumentBody({
    invoiceType,
    orderId,
    checkoutId,
    businessCaseId,
    invoicingDocumentId,
    isSellerAsSupplier,
  });

  const [submitCorrectiveTaxDocumentToBackend] = useSubmitCorrectiveTaxDocument();

  const submitCorrectiveTaxDocument = async (formData: AccountingDocumentFormValues) => {
    const {
      businessCaseId: validatedBusinessCaseId,
      orderId: validatedOrderId,
      paymentId,
      type,
    } = getValidatedCheckoutSearchParams(searchParams);
    const requestBody = getRequestBody(formData, summary);

    await submitCorrectiveTaxDocumentToBackend({
      businessCaseId: validatedBusinessCaseId,
      orderId: validatedOrderId,
      paymentId,
      type,
      requestBody,
      onSuccess: () =>
        navigate(
          composePath(businessCaseRoutes.checkout, {
            params: {id: validatedBusinessCaseId},
            queryParams: {CheckoutOrder: 'payment', createdCorrectiveDocumentType: type},
          })
        ),
    });
  };

  return [submitCorrectiveTaxDocument] as const;
};
